import React, { Component } from 'react';
import styled from '@emotion/styled';
import SearchIcon from './icons/SearchIcon';
import DeleteCrossIcon from './icons/DeleteCrossIcon';
import { PrimaryButton } from '../../buttons/PrimaryButton';
import ShareInputField from './ShareInputField';
import ShareStandardInputField from './ShareStandardInputField';

const ModalBody = styled.div`
  background: #fff;
  padding: 10px 2rem;
  display: flex;
  flex-direction: column;
`;

const BodyHeading = styled.div`
  color: #007db8;
  font-size: 1.1rem;
  font-weight: 500;
  width: 100%;
  border-bottom: 1px solid #bdcad2;
`;

const TabTitle = styled.h4`
  width: 33%;
  border-bottom: 3px solid #007db8;
  padding-top: 6px;
  padding-left: 6px;
  margin: 0px;
  font-weight: 600;
  padding-bottom: 11px;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 70%;
  justify-content: space-between;
  font-size: 0.9rem;

  & > div {
    display: flex;
    flex-direction: column;
    & > label {
      font-size: 1rem;
      font-weight: 500;
      margin-bottom: 10px;
    }
    & > input[type='text'] {
      height: 45px;
      border: 2px solid #bdcad2;
    }
  }
`;

const BodyContent = styled.div`
  font-size: 0.8rem;
  color: #566072;
  height: auto;
`;

const BodyFooter = styled.div`
  text-align: right;
  height: 80px;
  padding-top: 20px;
`;

const SearchInput = styled.div`
  position: relative;
  & > span:nth-of-type(1) {
    position: absolute;
    top: 40px;
    left: 10px;
  }
`;

const UserChip = styled.div`
  background: #eeeeee;
  color: #007db8;
  height: 30px;
  border-radius: 100px;
  font-size: 0.75rem;
  text-align: center;
  padding: 0.45rem;
  margin-right: 0.3rem;
  margin-bottom: 0.3rem;

  label {
    margin-left: 5px;
  }

  span {
    cursor: pointer;
  }
`;

const SelectedList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const SharedMessage = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 180px;
  justify-content: space-evenly;

  span {
    margin-top: 5px;
  }

  button {
    align-self: flex-end;
  }
`;
interface Props {
  closeFn: () => void;
  projectId: number;
  apiUrl?: string;
  userIsAdmin?: boolean;
}

interface State {
  showFirstTab: boolean;
  typeAhead: string[];
  selectedEmail: string[];
  recentRecipientList: string[];
  invalidEmails: string;
  showSharedMessage: boolean;
  showErrorMessage: boolean;
}

interface ProjectShareResponse {
  Data: {
    PartialErrorsList: { ObjectDisplayName: string }[];
  };
}

class ShareModal extends Component<Props, State> {
  state = {
    showFirstTab: true,
    typeAhead: [],
    selectedEmail: [],
    recentRecipientList: [],
    invalidEmails: '',
    showSharedMessage: false,
    showErrorMessage: false,
  } as State;

  showRecentRecipients = () => {
    this.setState(state => {
      return {
        ...state,
      };
    });
  };

  addRecipientToList = (selected: string) => {
    const selectedList = this.state.selectedEmail.concat(selected);

    this.setState(state => {
      return { ...state, typeAhead: [], selectedEmail: selectedList };
    });
  };

  removeRecipientFromlist = (key: number) => {
    const list = this.state.selectedEmail.filter((d, i) => i !== key);
    this.setState(state => {
      return { ...state, selectedEmail: list };
    });
  };

  share = async () => {
    const url = this.props.apiUrl
      ? `${this.props.apiUrl}/api/projects/share`
      : `/api/projects/share`;
    const payload = {
      projectId: this.props.projectId,
      ShareType: 'link',
      Name: '',
      Anonymize: false,
      EmailAddresses: this.state.selectedEmail,
      ConcierageSelected: false,
    };
    const settings = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };

    const response = await fetch(url, settings);
    const json: ProjectShareResponse = await response.json();

    if (response.status === 200 && json.Data.PartialErrorsList.length === 0) {
      this.setState(state => {
        return { ...state, showSharedMessage: true };
      });
    } else if (response.status === 200 && json.Data.PartialErrorsList.length > 0) {
      const invalidEmails = json.Data.PartialErrorsList.map(d => {
        return d.ObjectDisplayName;
      }).join(', ');
      this.setState(state => {
        return { ...state, showErrorMessage: true, invalidEmails: invalidEmails };
      });
    } else {
      this.setState(state => {
        return { ...state, showErrorMessage: true };
      });
    }
  };

  render() {
    return (
      <>
        <ModalBody>
          {!this.state.showSharedMessage && !this.state.showErrorMessage && (
            <>
              <BodyHeading>
                <TabTitle>Give access to this project</TabTitle>
              </BodyHeading>
              <BodyContent>
                <Panel>
                  <p>Changes can be overwritten by any users with access to the project.</p>

                  <SearchInput>
                    <label>Select recipients</label>
                    {this.props.userIsAdmin && (
                      <>
                        <SearchIcon />
                        <ShareInputField
                          apiUrl={this.props.apiUrl}
                          projectId={this.props.projectId}
                          listItemClick={this.addRecipientToList}
                        />
                      </>
                    )}
                    {!this.props.userIsAdmin && (
                      <ShareStandardInputField
                        projectId={this.props.projectId}
                        addItemClick={this.addRecipientToList}
                      />
                    )}

                    {this.state.selectedEmail.length > 0 && (
                      <SelectedList>
                        {this.state.selectedEmail.map((d, i) => {
                          return (
                            <UserChip
                              key={i}
                              onClick={() => {
                                this.removeRecipientFromlist(i);
                              }}
                            >
                              {d} <DeleteCrossIcon />
                            </UserChip>
                          );
                        })}
                      </SelectedList>
                    )}
                  </SearchInput>
                </Panel>
              </BodyContent>
              <BodyFooter>
                <PrimaryButton
                  clickFn={this.share}
                  disabled={this.state.selectedEmail.length === 0}
                >
                  Share
                </PrimaryButton>
              </BodyFooter>
            </>
          )}
          {this.state.showSharedMessage && (
            <SharedMessage>
              The following users received copies of this project:
              {this.state.selectedEmail.map((d, i) => {
                return <span key={i}>{d}</span>;
              })}
              <PrimaryButton clickFn={this.props.closeFn}>Ok</PrimaryButton>
            </SharedMessage>
          )}
          {this.state.showErrorMessage && this.state.invalidEmails.length === 0 && (
            <SharedMessage>
              <p>Something has gone wrong. Please try again.</p>
              <p>
                Contact <a href="https://support.liveoptics.com/">support</a> if the issue persists.
              </p>
              <PrimaryButton clickFn={this.props.closeFn}>Ok</PrimaryButton>
            </SharedMessage>
          )}
          {this.state.showErrorMessage && this.state.invalidEmails.length > 0 && (
            <SharedMessage>
              <h3>User not found</h3>
              <p>
                Could not share project with the following emails: {this.state.invalidEmails}. A
                project can only be shared with another user. Check the spelling of the entered
                email address and ensure it is a valid user.
              </p>
              <PrimaryButton clickFn={this.props.closeFn}>Ok</PrimaryButton>
            </SharedMessage>
          )}
        </ModalBody>
      </>
    );
  }
}

export default ShareModal;
